import { Injectable } from '@angular/core';
import MediaInfo from 'mediainfo.js';

import { logger } from '../../helpers/logger';
export enum ComparisonType {
  LessThan = 1,
  LessThanEqual = 2,
  Exact = 3,
  GreaterThanEqual = 4,
  GreaterThan = 5,
}

@Injectable({
  providedIn: 'root',
})
export class VideoValidationService {
  // Private fields
  private mediaInfoInstance: any;
  //This is only really used if someone tries to pass a maximum value less than or equal to zero.
  private readonly DEFAULT_MAX_BITRATE_MBPS = 10;
  private readonly DEFAULT_FRAMERATES = [30, 60];
  // Cache for storing analysis results with a short TTL (1 minute)
  private analysisCache = new Map<
    string,
    {
      timestamp: number;
      result: any;
    }
  >();

  // Map to track in-progress analysis promises to prevent duplicate concurrent analyses
  private pendingAnalyses = new Map<string, Promise<any>>();

  constructor() {
    // Initialize MediaInfo when service is created
    this.initMediaInfo();
  }

  /**
   * Validates a video file's resolution against specified dimensions
   * @param file The video file to validate
   * @param width The width to compare against
   * @param height The height to compare against
   * @param comparisonType The type of comparison to perform (less than, exactly, more than)
   * @returns Promise with validation result
   */
  public async validateVideoResolution(
    file: File,
    width: number,
    height: number,
    comparisonType: ComparisonType = ComparisonType.Exact
  ): Promise<{ valid: boolean; message?: string; width?: number; height?: number }> {
    try {
      const result = await this.analyzeVideoFile(file);

      if (!result.media?.track) {
        return { valid: false, message: 'Could not analyze video file' };
      }

      // Find video track
      const videoTrack = result.media.track.find((track: any) => track['@type'] === 'Video');

      if (!videoTrack) {
        return { valid: false, message: 'No video track found in file' };
      }

      // Extract resolution
      let videoWidth = 0;
      let videoHeight = 0;

      if (videoTrack.Width && videoTrack.Height) {
        videoWidth = parseInt(videoTrack.Width, 10);
        videoHeight = parseInt(videoTrack.Height, 10);
      }

      if (videoWidth === 0 || videoHeight === 0) {
        return { valid: false, message: 'Could not determine video resolution' };
      }

      let valid = false;
      let comparisonMessage = '';

      switch (comparisonType) {
        case ComparisonType.LessThan:
          valid = videoWidth < width && videoHeight < height;
          comparisonMessage = 'less than';
          break;
        case ComparisonType.LessThanEqual:
          valid = videoWidth <= width && videoHeight <= height;
          comparisonMessage = 'less than or equal to';
          break;
        case ComparisonType.Exact:
          valid = videoWidth === width && videoHeight === height;
          comparisonMessage = '';
          break;
        case ComparisonType.GreaterThanEqual:
          valid = videoWidth >= width && videoHeight >= height;
          comparisonMessage = 'greater than or equal to';
          break;
        case ComparisonType.GreaterThan:
          valid = videoWidth > width && videoHeight > height;
          comparisonMessage = 'more than';
          break;
      }

      return {
        valid,
        width: videoWidth,
        height: videoHeight,
        message: valid
          ? undefined
          : `Video resolution (${videoWidth}x${videoHeight}) must be ${comparisonMessage} ${width}x${height}`,
      };
    } catch (error) {
      logger.error('Error analyzing video:', error);
      return { valid: false, message: 'Error analyzing video file' };
    }
  }

  /**
   * Validates if a video file's bitrate meets the specified criteria
   * @param file The video file to validate
   * @param bitrateMbps The bitrate value in Mbps to compare against
   * @param comparisonType The type of comparison to perform
   * @param errorMarginMbps The error margin in Mbps to allow for comparison (default: 0)
   * @returns Promise with validation result
   */
  public async validateVideoBitrate(
    file: File,
    bitrateMbps: number,
    comparisonType: ComparisonType = ComparisonType.LessThan,
    errorMarginMbps: number = 0
  ): Promise<{ valid: boolean; message?: string; bitrate?: number }> {
    try {
      const result = await this.analyzeVideoFile(file);

      if (!result.media?.track) {
        return { valid: false, message: 'Could not analyze video file' };
      }

      // Find video track
      const videoTrack = result.media.track.find((track: any) => track['@type'] === 'Video');

      if (!videoTrack) {
        return { valid: false, message: 'No video track found in file' };
      }

      // Extract bitrate - MediaInfo may provide it in different properties
      let bitrate = 0;

      if (videoTrack.BitRate) {
        bitrate = parseInt(videoTrack.BitRate, 10);
      } else if (result.media.track[0].OverallBitRate) {
        // Sometimes overall bitrate is in the General track (index 0)
        bitrate = parseInt(result.media.track[0].OverallBitRate, 10);
      }

      if (bitrate === 0) {
        return { valid: false, message: 'Could not determine video bitrate' };
      }

      const calculatedBitrateMbps = bitrateMbps && bitrateMbps > 0 ? bitrateMbps : this.DEFAULT_MAX_BITRATE_MBPS;
      const calculatedBitrateBps = this.toBps(calculatedBitrateMbps);

      // Convert error margin to bps for calculations
      const marginBps = this.toBps(errorMarginMbps);
      let valid = false;
      let comparisonMessage = '';

      switch (comparisonType) {
        case ComparisonType.LessThan:
          valid = bitrate < calculatedBitrateBps + marginBps;
          comparisonMessage = 'less than';
          break;
        case ComparisonType.LessThanEqual:
          valid = bitrate <= calculatedBitrateBps + marginBps;
          comparisonMessage = 'less than or equal to';
          break;
        case ComparisonType.Exact:
          valid = Math.abs(bitrate - calculatedBitrateBps) <= marginBps;
          comparisonMessage = marginBps === 0 ? '' : 'approximately';
          break;
        case ComparisonType.GreaterThanEqual:
          valid = bitrate >= calculatedBitrateBps - marginBps;
          comparisonMessage = 'greater than or equal to';
          break;
        case ComparisonType.GreaterThan:
          valid = bitrate > calculatedBitrateBps - marginBps;
          comparisonMessage = 'more than';
          break;
      }

      return {
        valid,
        bitrate,
        message: valid
          ? undefined
          : `Video bitrate (${this.toMbps(bitrate)}Mbps) must be ${comparisonMessage} ${calculatedBitrateMbps}Mbps`,
      };
    } catch (error) {
      logger.error('Error analyzing video:', error);
      return { valid: false, message: 'Error analyzing video file' };
    }
  }

  private toBps(mbps: number) {
    return mbps * 1000000;
  }

  private toMbps(bps: number) {
    return Math.round(bps / 1000000);
  }

  /**
   * Validates if a video file's framerate matches one of the allowed values
   * @param file The video file to validate
   * @param allowedFramerates Array of allowed framerates (e.g. [30, 60])
   * @returns Promise with validation result
   */
  public async validateVideoFramerate(
    file: File,
    allowedFramerates: number[] = this.DEFAULT_FRAMERATES
  ): Promise<{ valid: boolean; message?: string; framerate?: number }> {
    try {
      const result = await this.analyzeVideoFile(file);

      if (!result.media?.track) {
        return { valid: false, message: 'Could not analyze video file' };
      }

      // Find video track
      const videoTrack = result.media.track.find((track: any) => track['@type'] === 'Video');

      if (!videoTrack) {
        return { valid: false, message: 'No video track found in file' };
      }

      // Extract framerate - MediaInfo may provide it in different properties
      let framerate = 0;

      if (videoTrack.FrameRate) {
        framerate = parseFloat(videoTrack.FrameRate);
      } else if (videoTrack.FrameRate_Original) {
        framerate = parseFloat(videoTrack.FrameRate_Original);
      }

      if (framerate === 0) {
        return { valid: false, message: 'Could not determine video framerate' };
      }

      // Check if framerate matches any of the allowed values
      const valid = allowedFramerates.some(rate => Math.abs(framerate - rate) < 0.1);

      // Create appropriate message based on number of allowed framerates
      let message = '';
      if (!valid) {
        if (allowedFramerates.length === 1) {
          message = `Video framerate (${framerate.toFixed(2)} fps) must be ${allowedFramerates[0]} fps`;
        } else {
          message = `Video framerate (${framerate.toFixed(2)} fps) must be one of: ${allowedFramerates.join(', ')} fps`;
        }
      }

      return {
        valid,
        framerate,
        message: valid ? undefined : message,
      };
    } catch (error) {
      logger.error('Error analyzing video:', error);
      return { valid: false, message: 'Error analyzing video file' };
    }
  }

  // Private methods
  private async initMediaInfo() {
    this.mediaInfoInstance = await MediaInfo({
      format: 'object',
      locateFile: (path: string) => {
        return `/mediainfo/${path}`;
      },
    });
  }

  /**
   * Creates a function to read chunks of a file
   * @param file The file to read chunks from
   * @returns A function that reads chunks of the file
   */
  private createReadChunkFunction(file: File) {
    return (size: number, offset: number) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
          if (event.target?.result) {
            resolve(new Uint8Array(event.target.result as ArrayBuffer));
          } else {
            reject(new Error('Failed to read chunk'));
          }
        };
        reader.onerror = error => reject(error);
        reader.readAsArrayBuffer(file.slice(offset, offset + size));
      });
    };
  }

  /**
   * Gets a unique cache key for a file
   * @param file The file to get a cache key for
   * @returns A string that uniquely identifies the file
   */
  private getCacheKey(file: File): string {
    // Use file properties to create a unique key
    return `${file.name}_${file.size}_${file.lastModified}`;
  }

  /**
   * Gets a cached analysis result for a file if it exists and is not expired
   * @param file The file to get a cached analysis for
   * @returns The cached analysis result or null if not found or expired
   */
  private getCachedAnalysis(file: File): any | null {
    const key = this.getCacheKey(file);
    const cached = this.analysisCache.get(key);

    if (!cached) return null;

    // Check if cache entry is expired (older than 1 minute)
    if (Date.now() - cached.timestamp > 60000) {
      this.analysisCache.delete(key);
      return null;
    }

    return cached.result;
  }

  /**
   * Stores an analysis result in the cache
   * @param file The file that was analyzed
   * @param result The analysis result
   */
  private setCachedAnalysis(file: File, result: any): void {
    const key = this.getCacheKey(file);
    this.analysisCache.set(key, {
      timestamp: Date.now(),
      result,
    });
  }

  /**
   * Analyzes a video file and caches the result
   * Handles concurrent requests for the same file to prevent duplicate analyses
   * @param file The video file to analyze
   * @returns The analysis result
   */
  private async analyzeVideoFile(file: File): Promise<any> {
    // Check cache first
    const cachedResult = this.getCachedAnalysis(file);
    if (cachedResult) {
      return cachedResult;
    }

    // Get cache key for this file
    const key = this.getCacheKey(file);

    // Check if there's already an analysis in progress for this file
    const pendingAnalysis = this.pendingAnalyses.get(key);
    if (pendingAnalysis) {
      return pendingAnalysis;
    }

    // Create a new analysis promise
    const analysisPromise = (async () => {
      if (!this.mediaInfoInstance) {
        await this.initMediaInfo();
      }

      try {
        const getSize = () => file.size;
        const readChunk = this.createReadChunkFunction(file);

        const result = await this.mediaInfoInstance.analyzeData(getSize, readChunk);

        // Store in cache
        this.setCachedAnalysis(file, result);

        // Remove from pending analyses
        this.pendingAnalyses.delete(key);

        return result;
      } catch (error) {
        // Remove from pending analyses on error
        this.pendingAnalyses.delete(key);
        logger.error('Error analyzing video:', error);
        throw error;
      }
    })();

    // Store the promise in the pending analyses map
    this.pendingAnalyses.set(key, analysisPromise);

    return analysisPromise;
  }
}
